import React, { useMemo, useState } from 'react';

const MESES = [
  ['Jan', 'Janeiro'],
  ['Fev', 'Fevereiro'],
  ['Mar', 'Março'],
  ['Abr', 'Abril'],
  ['Mai', 'Maio'],
  ['Jun', 'Junho'],
  ['Jul', 'Julho'],
  ['Ago', 'Agosto'],
  ['Set', 'Setembro'],
  ['Out', 'Outubro'],
  ['Nov', 'Novembro'],
  ['Dez', 'Dezembro'],
];

const fmt = valor =>
  Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

function dataBR(valor) {
  if (!valor) return '—';
  const texto = String(valor).slice(0, 10);
  const partes = texto.split('-');
  if (partes.length !== 3) return texto;
  return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

function valorTexto(valor) {
  return String(valor || '').trim() || '—';
}

function Campo({ rotulo, valor }) {
  return (
    <div>
      <div className="kpi-label">{rotulo}</div>
      <div style={{ marginTop: 4, fontSize: 13 }}>
        {valorTexto(valor)}
      </div>
    </div>
  );
}

export default function CrmHistoricoComercial({
  oportunidades = [],
  etapas = [],
}) {
  const [busca, setBusca] = useState('');
  const [mes, setMes] = useState('');
  const [etapa, setEtapa] = useState('');
  const [vendedor, setVendedor] = useState('');
  const [selecionada, setSelecionada] = useState(null);

  const historico = useMemo(
    () => oportunidades.filter(item => item.origem_historico),
    [oportunidades]
  );

  const vendedores = useMemo(() => {
    const nomes = historico
      .map(item => item.vendedor_externo)
      .filter(Boolean)
      .map(nome => String(nome).trim());

    return [...new Set(nomes)].sort((a, b) =>
      a.localeCompare(b, 'pt-BR')
    );
  }, [historico]);

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    return historico.filter(item => {
      if (mes && item.planilha_aba !== mes) return false;
      if (etapa && item.etapa_codigo !== etapa) return false;
      if (vendedor && item.vendedor_externo !== vendedor) return false;

      if (!termo) return true;

      return [
        item.titulo,
        item.empresa_razao_social,
        item.empresa_nome_fantasia,
        item.referencia_comercial,
        item.status_original,
        item.etapa_original,
        item.responsavel_atual,
        item.observacoes_historicas,
      ].some(valor =>
        String(valor || '').toLowerCase().includes(termo)
      );
    });
  }, [historico, busca, mes, etapa, vendedor]);

  const valorTotal = filtradas.reduce(
    (soma, item) => soma + Number(item.valor_estimado || 0),
    0
  );

  function limparFiltros() {
    setBusca('');
    setMes('');
    setEtapa('');
    setVendedor('');
  }

  return (
    <>
      <div className="card" style={{ marginBottom: 18 }}>
        <div
          className="card-head"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div className="card-title">Histórico comercial de 2026</div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--muted)',
                marginTop: 4,
              }}
            >
              Dados incorporados da antiga planilha comercial.
            </div>
          </div>

          <button className="btn btn-ghost" onClick={limparFiltros}>
            Limpar filtros
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'minmax(220px, 2fr) repeat(3, minmax(150px, 1fr))',
            gap: 10,
            padding: '0 16px 16px',
          }}
        >
          <input
            className="search-input"
            placeholder="Buscar cliente, cotação, status ou observação..."
            value={busca}
            onChange={evento => setBusca(evento.target.value)}
          />

          <select
            value={mes}
            onChange={evento => setMes(evento.target.value)}
          >
            <option value="">Todos os meses</option>
            {MESES.map(([codigo, nome]) => (
              <option key={codigo} value={codigo}>{nome}</option>
            ))}
          </select>

          <select
            value={etapa}
            onChange={evento => setEtapa(evento.target.value)}
          >
            <option value="">Todas as etapas</option>
            {etapas.map(item => (
              <option key={item.codigo} value={item.codigo}>
                {item.nome}
              </option>
            ))}
          </select>

          <select
            value={vendedor}
            onChange={evento => setVendedor(evento.target.value)}
          >
            <option value="">Todos os vendedores</option>
            {vendedores.map(nome => (
              <option key={nome} value={nome}>{nome}</option>
            ))}
          </select>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
          marginBottom: 18,
        }}
      >
        <div className="kpi-card">
          <div className="kpi-label">Registros encontrados</div>
          <div className="kpi-value">{filtradas.length}</div>
          <div className="kpi-sub">de {historico.length} importados</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-label">Valor encontrado</div>
          <div className="kpi-value" style={{ fontSize: 22 }}>
            {fmt(valorTotal)}
          </div>
          <div className="kpi-sub">conforme valores da planilha</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-label">Empresas no resultado</div>
          <div className="kpi-value">
            {new Set(filtradas.map(item => item.empresa_id)).size}
          </div>
          <div className="kpi-sub">clientes distintos</div>
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Mês</th>
                <th>Solicitação</th>
                <th>Cliente</th>
                <th>Cotação / Proposta</th>
                <th>Vendedor</th>
                <th>Status original</th>
                <th>Etapa atual</th>
                <th>Valor</th>
                <th>Follow-up</th>
                <th>Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {!filtradas.length ? (
                <tr>
                  <td
                    colSpan={10}
                    style={{ textAlign: 'center', padding: 30 }}
                  >
                    Nenhum registro encontrado.
                  </td>
                </tr>
              ) : filtradas.map(item => (
                <tr key={item.id}>
                  <td>
                    {item.planilha_aba}
                    <div
                      style={{
                        fontSize: 10,
                        color: 'var(--muted)',
                      }}
                    >
                      Linha {item.planilha_linha}
                    </div>
                  </td>
                  <td>{dataBR(item.data_solicitacao)}</td>
                  <td>
                    <strong>
                      {item.empresa_nome_fantasia ||
                        item.empresa_razao_social}
                    </strong>
                  </td>
                  <td>{item.referencia_comercial || item.titulo}</td>
                  <td>{item.vendedor_externo || '—'}</td>
                  <td>{item.status_original || '—'}</td>
                  <td>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '3px 7px',
                        borderRadius: 999,
                        color: '#fff',
                        background:
                          item.etapa_cor || 'var(--muted)',
                        fontSize: 10,
                      }}
                    >
                      {item.etapa_nome}
                    </span>
                  </td>
                  <td><strong>{fmt(item.valor_estimado)}</strong></td>
                  <td>{dataBR(item.proximo_followup)}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => setSelecionada(item)}
                    >
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selecionada && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{ maxWidth: 860, width: '94%' }}
          >
            <div
              className="modal-head"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div>
                <div className="modal-title">
                  {selecionada.referencia_comercial ||
                    selecionada.titulo}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--muted)',
                    marginTop: 4,
                  }}
                >
                  {selecionada.empresa_nome_fantasia ||
                    selecionada.empresa_razao_social}
                </div>
              </div>

              <button
                className="modal-close"
                onClick={() => setSelecionada(null)}
                style={{
                  border: 0,
                  background: 'transparent',
                  fontSize: 24,
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fit, minmax(190px, 1fr))',
                  gap: 18,
                }}
              >
                <Campo
                  rotulo="Data da solicitação"
                  valor={dataBR(selecionada.data_solicitacao)}
                />
                <Campo
                  rotulo="Data de envio"
                  valor={dataBR(selecionada.data_envio)}
                />
                <Campo
                  rotulo="Valor"
                  valor={fmt(selecionada.valor_estimado)}
                />
                <Campo
                  rotulo="Vendedor externo"
                  valor={selecionada.vendedor_externo}
                />
                <Campo
                  rotulo="Status original"
                  valor={selecionada.status_original}
                />
                <Campo
                  rotulo="Etapa original"
                  valor={selecionada.etapa_original}
                />
                <Campo
                  rotulo="Com quem estava"
                  valor={selecionada.responsavel_atual}
                />
                <Campo
                  rotulo="Etapa atual no CRM"
                  valor={selecionada.etapa_nome}
                />
                <Campo
                  rotulo="Data de aprovação"
                  valor={dataBR(selecionada.data_aprovacao)}
                />
                <Campo
                  rotulo="Data do pedido"
                  valor={dataBR(selecionada.data_pedido)}
                />
                <Campo
                  rotulo="Data de entrega"
                  valor={dataBR(selecionada.data_entrega)}
                />
                <Campo
                  rotulo="Último contato"
                  valor={dataBR(selecionada.ultimo_contato)}
                />
                <Campo
                  rotulo="Próximo follow-up"
                  valor={dataBR(selecionada.proximo_followup)}
                />
                <Campo
                  rotulo="Origem"
                  valor={`${selecionada.planilha_aba} · linha ${selecionada.planilha_linha}`}
                />
              </div>

              <div style={{ marginTop: 22 }}>
                <div className="kpi-label">
                  Observações e histórico
                </div>
                <div
                  style={{
                    marginTop: 8,
                    padding: 14,
                    borderRadius: 8,
                    background: 'var(--surface2)',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.5,
                  }}
                >
                  {selecionada.observacoes_historicas ||
                    'Nenhuma observação registrada.'}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => setSelecionada(null)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
